import React, { useEffect, useState } from 'react';
import { TopBar } from '../components/TopBar';
import { MainTabs } from '../components/MainTabs';
import { FilterPills } from '../components/FilterPills';
import { ClientCard } from '../components/ClientCard';
import { BottomNav } from '../components/BottomNav';
import { Plus, X, ChevronRight, User, Phone, Mail, MessageSquare, Tag, TrendingUp, MapPin, Home, DollarSign, Bed, Bath, Calendar, Search, SlidersHorizontal, Check, ChevronDown, Maximize, Building2 } from 'lucide-react';
type ClientCategory = 'New' | 'In-contact' | 'Negotiating' | 'Confirmed' | 'Closed';
const clients = [{
  id: 1,
  name: 'Ahmed Hamdy Mohamed',
  phone: '+20 123 456 7890',
  phoneCountry: 'EG',
  budget: '2M to 2.5 M EGP',
  type: 'BUY' as const,
  initials: 'AH',
  category: 'New' as ClientCategory,
  assignedDate: '2024-01-15',
  tags: ['Hot Lead', 'First Time Buyer'],
  labels: ['VIP', 'Urgent']
}, {
  id: 2,
  name: 'Sarah Ahmed',
  phone: '+20 111 222 3333',
  phoneCountry: 'EG',
  budget: '5M to 7 M EGP',
  type: 'BUY' as const,
  initials: 'SA',
  category: 'In-contact' as ClientCategory,
  assignedDate: '2024-01-10',
  tags: ['Investor'],
  labels: ['High Budget']
}, {
  id: 3,
  name: 'Mohamed Ali',
  phone: '+20 100 555 6666',
  phoneCountry: 'EG',
  budget: '15,000 to 20,000 EGP',
  type: 'RENT' as const,
  initials: 'MA',
  category: 'In-contact' as ClientCategory,
  assignedDate: '2024-01-12',
  tags: ['Rental'],
  labels: ['Follow Up']
}, {
  id: 4,
  name: 'Karim Mahmoud',
  phone: '+20 122 777 8888',
  phoneCountry: 'EG',
  budget: '3M to 4 M EGP',
  type: 'BUY' as const,
  initials: 'KM',
  category: 'Negotiating' as ClientCategory,
  assignedDate: '2024-01-08',
  tags: ['Hot Lead', 'Ready to Buy'],
  labels: ['VIP']
}, {
  id: 5,
  name: 'Nadia Hassan',
  phone: '+20 101 999 0000',
  phoneCountry: 'EG',
  budget: '1.5M to 2 M EGP',
  type: 'BUY' as const,
  initials: 'NH',
  category: 'Confirmed' as ClientCategory,
  assignedDate: '2024-01-05',
  tags: ['First Time Buyer'],
  labels: ['Confirmed']
}, {
  id: 6,
  name: 'Omar Khaled',
  phone: '+20 155 444 3333',
  phoneCountry: 'EG',
  budget: '8M to 10 M EGP',
  type: 'BUY' as const,
  initials: 'OK',
  category: 'Closed' as ClientCategory,
  assignedDate: '2024-01-01',
  tags: ['Investor', 'Repeat Client'],
  labels: ['High Budget', 'VIP']
}];
interface ClientsPageProps {
  onClientClick?: (id: number) => void;
  onNetworkClick?: () => void;
  onInventoryClick?: () => void;
  onRMIClick?: () => void;
  onPropertiesClick?: () => void;
  onCalendarClick?: () => void;
  onChatClick?: () => void;
  onDashboardClick?: () => void;
  onNotificationsClick?: () => void;
  onProfileClick?: () => void;
}
export function ClientsPage({
  onClientClick,
  onNetworkClick,
  onInventoryClick,
  onRMIClick,
  onPropertiesClick,
  onCalendarClick,
  onChatClick,
  onDashboardClick,
  onNotificationsClick,
  onProfileClick
}: ClientsPageProps) {
  const [activeFilter, setActiveFilter] = useState<string>('New');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [addClientStep, setAddClientStep] = useState<1 | 2>(1);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  // Advanced search filters
  const [assignedDateFrom, setAssignedDateFrom] = useState('');
  const [assignedDateTo, setAssignedDateTo] = useState('');
  const [phoneCountryFilter, setPhoneCountryFilter] = useState<'is' | 'is not'>('is');
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedSalesStages, setSelectedSalesStages] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  // Available options
  const availableCountries = ['EG', 'SA', 'AE', 'KW', 'QA', 'BH', 'OM'];
  const availableSalesStages = ['New', 'In-contact', 'Negotiating', 'Confirmed', 'Closed'];
  const availableTags = ['Hot Lead', 'First Time Buyer', 'Investor', 'Rental', 'Ready to Buy', 'Repeat Client'];
  const availableLabels = ['VIP', 'Urgent', 'High Budget', 'Follow Up', 'Confirmed'];
  // Form state - Step 1
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [leadSource, setLeadSource] = useState('');
  const [marketingCampaign, setMarketingCampaign] = useState('');
  const [notes, setNotes] = useState('');
  // Form state - Step 2 (Updated)
  const [transactionType, setTransactionType] = useState('BUY');
  const [clientStage, setClientStage] = useState('New');
  const [interestedProjects, setInterestedProjects] = useState<string[]>([]);
  const [bedrooms, setBedrooms] = useState('');
  const [bathrooms, setBathrooms] = useState('');
  const [minBudget, setMinBudget] = useState('');
  const [maxBudget, setMaxBudget] = useState('');
  const [minArea, setMinArea] = useState('');
  const [maxArea, setMaxArea] = useState('');
  const [location, setLocation] = useState('');
  // Available projects for multi-select
  const availableProjects = ['Mountain View iCity', 'Palm Hills October', 'Hyde Park New Cairo', 'Madinety', 'Mivida', 'Sodic East', 'Taj City', 'Sarai', 'Bloomfields', 'IL Bosco'];
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const handleTabChange = (tab: 'home' | 'clients' | 'properties') => {
    if (tab === 'home' && onDashboardClick) {
      onDashboardClick();
    } else if (tab === 'properties' && onPropertiesClick) {
      onPropertiesClick();
    }
  };
  const handleFilterChange = (filter: string) => {
    setActiveFilter(filter);
  };
  const handleAddClient = () => {
    setShowAddClientModal(true);
    setAddClientStep(1);
  };
  const handleCloseModal = () => {
    setShowAddClientModal(false);
    setAddClientStep(1);
    // Reset form
    setClientName('');
    setClientPhone('');
    setClientEmail('');
    setLeadSource('');
    setMarketingCampaign('');
    setNotes('');
    setTransactionType('BUY');
    setClientStage('New');
    setInterestedProjects([]);
    setBedrooms('');
    setBathrooms('');
    setMinBudget('');
    setMaxBudget('');
    setMinArea('');
    setMaxArea('');
    setLocation('');
  };
  const handleNextStep = () => {
    if (addClientStep === 1) {
      setAddClientStep(2);
    }
  };
  const handlePreviousStep = () => {
    if (addClientStep === 2) {
      setAddClientStep(1);
    }
  };
  const handleSubmitClient = () => {
    alert('Client added successfully!');
    handleCloseModal();
  };
  const handleClearAdvancedFilters = () => {
    setAssignedDateFrom('');
    setAssignedDateTo('');
    setPhoneCountryFilter('is');
    setSelectedCountries([]);
    setSelectedSalesStages([]);
    setSelectedTags([]);
    setSelectedLabels([]);
  };
  const handleApplyAdvancedFilters = () => {
    setShowAdvancedSearch(false);
  };
  const toggleCountry = (country: string) => {
    setSelectedCountries(prev => prev.includes(country) ? prev.filter(c => c !== country) : [...prev, country]);
  };
  const toggleSalesStage = (stage: string) => {
    setSelectedSalesStages(prev => prev.includes(stage) ? prev.filter(s => s !== stage) : [...prev, stage]);
  };
  const toggleTag = (tag: string) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };
  const toggleLabel = (label: string) => {
    setSelectedLabels(prev => prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]);
  };
  const toggleProject = (project: string) => {
    setInterestedProjects(prev => prev.includes(project) ? prev.filter(p => p !== project) : [...prev, project]);
  };
  const hasActiveAdvancedFilters = assignedDateFrom || assignedDateTo || selectedCountries.length > 0 || selectedSalesStages.length > 0 || selectedTags.length > 0 || selectedLabels.length > 0;
  const filteredClients = clients.filter(client => {
    // Basic search
    const matchesCategory = client.category === activeFilter;
    const matchesSearch = searchQuery === '' || client.name.toLowerCase().includes(searchQuery.toLowerCase()) || client.phone.toLowerCase().includes(searchQuery.toLowerCase());
    // Advanced filters
    const matchesDateFrom = !assignedDateFrom || new Date(client.assignedDate) >= new Date(assignedDateFrom);
    const matchesDateTo = !assignedDateTo || new Date(client.assignedDate) <= new Date(assignedDateTo);
    const matchesCountry = selectedCountries.length === 0 || (phoneCountryFilter === 'is' ? selectedCountries.includes(client.phoneCountry) : !selectedCountries.includes(client.phoneCountry));
    const matchesSalesStage = selectedSalesStages.length === 0 || selectedSalesStages.includes(client.category);
    const matchesTags = selectedTags.length === 0 || selectedTags.some(tag => client.tags.includes(tag));
    const matchesLabels = selectedLabels.length === 0 || selectedLabels.some(label => client.labels.includes(label));
    return matchesCategory && matchesSearch && matchesDateFrom && matchesDateTo && matchesCountry && matchesSalesStage && matchesTags && matchesLabels;
  });
  return <div className="min-h-screen bg-neutral-bg pb-20 font-sans w-full" data-id="element-839">
      <div className="w-full mx-auto bg-white min-h-screen shadow-2xl relative" data-id="element-840">
        <TopBar onCalendarClick={onCalendarClick} onChatClick={onChatClick} data-id="element-841" />
        <MainTabs activeTab="clients" onTabChange={handleTabChange} data-id="element-842" />

        {/* Search Bar */}
        <div className="px-6 pt-4 pb-2" data-id="element-843">
          <div className="flex gap-2" data-id="element-844">
            <div className="relative flex-1" data-id="element-845">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-846" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search by name or phone number" className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary shadow-sm" data-id="element-847" />
            </div>
            <button onClick={() => setShowAdvancedSearch(true)} className={`px-4 py-3 rounded-xl border-2 transition-colors flex items-center gap-2 ${hasActiveAdvancedFilters ? 'bg-secondary border-secondary text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-secondary'}`} data-id="element-848">
              <SlidersHorizontal className="w-5 h-5" data-id="element-849" />
              {hasActiveAdvancedFilters && <span className="text-xs font-bold bg-white text-secondary rounded-full w-5 h-5 flex items-center justify-center" data-id="element-850">
                  {[selectedCountries, selectedSalesStages, selectedTags, selectedLabels].filter(arr => arr.length > 0).length}
                </span>}
            </button>
          </div>
        </div>

        {/* Add Client Button */}
        <div className="px-6 pb-2" data-id="element-851">
          <button onClick={handleAddClient} className="w-full bg-gradient-to-r from-secondary to-secondary-dark hover:from-secondary-dark hover:to-secondary text-white font-bold py-3 rounded-xl transition-colors shadow-lg flex items-center justify-center gap-2" data-id="element-852">
            <Plus className="w-5 h-5" data-id="element-853" />
            Add New Client
          </button>
        </div>

        <FilterPills activeFilter={activeFilter} onFilterChange={handleFilterChange} data-id="element-854" />

        <div className="divide-y divide-gray-50" data-id="element-855">
          {filteredClients.length > 0 ? filteredClients.map(client => <ClientCard key={client.id} id={client.id} name={client.name} budget={client.budget} type={client.type} initials={client.initials} onClick={onClientClick} data-id="element-856" />) : <div className="flex flex-col items-center justify-center py-16 px-4" data-id="element-857">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4" data-id="element-858">
                <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-859">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" data-id="element-860" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-neutral-text mb-1" data-id="element-861">
                No Clients Found
              </h3>
              <p className="text-sm text-gray-500 text-center" data-id="element-862">
                {searchQuery ? `No clients match "${searchQuery}"` : `There are no clients in the "${activeFilter}" category yet.`}
              </p>
            </div>}
        </div>

        {/* Add Client Modal */}
        {showAddClientModal && <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-id="element-863">
            <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl flex flex-col max-h-[90vh] sm:max-h-[85vh]" data-id="element-864">
              {/* Header - Fixed */}
              <div className="flex-shrink-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between rounded-t-3xl sm:rounded-t-2xl" data-id="element-865">
                <div className="flex items-center gap-3" data-id="element-866">
                  <h2 className="text-xl font-bold text-neutral-text" data-id="element-867">
                    Add New Client
                  </h2>
                  <span className="text-sm text-gray-500" data-id="element-868">
                    Step {addClientStep} of 2
                  </span>
                </div>
                <button onClick={handleCloseModal} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-neutral-bg rounded-full transition-colors" data-id="element-869">
                  <X className="w-5 h-5" data-id="element-870" />
                </button>
              </div>

              {/* Progress Indicator - Fixed */}
              <div className="flex-shrink-0 px-6 pt-4 pb-2 bg-white" data-id="element-871">
                <div className="flex items-center gap-2" data-id="element-872">
                  <div className={`flex-1 h-1.5 rounded-full ${addClientStep >= 1 ? 'bg-secondary' : 'bg-gray-200'}`} data-id="element-873" />
                  <div className={`flex-1 h-1.5 rounded-full ${addClientStep >= 2 ? 'bg-secondary' : 'bg-gray-200'}`} data-id="element-874" />
                </div>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto" data-id="element-875">
                {/* Step 1: Contact Information */}
                {addClientStep === 1 && <div className="p-6 space-y-5" data-id="element-876">
                    <div data-id="element-877">
                      <h3 className="font-bold text-neutral-text mb-4 flex items-center gap-2" data-id="element-878">
                        <User className="w-5 h-5 text-secondary" data-id="element-879" />
                        Contact Information
                      </h3>

                      <div className="space-y-4" data-id="element-880">
                        <div data-id="element-881">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-882">
                            Full Name *
                          </label>
                          <input type="text" value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Enter client name" className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-883" />
                        </div>

                        <div data-id="element-884">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-885">
                            Phone Number *
                          </label>
                          <div className="relative" data-id="element-886">
                            <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-887" />
                            <input type="tel" value={clientPhone} onChange={e => setClientPhone(e.target.value)} placeholder="+20 123 456 7890" className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-888" />
                          </div>
                        </div>

                        <div data-id="element-889">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-890">
                            Email Address
                          </label>
                          <div className="relative" data-id="element-891">
                            <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-892" />
                            <input type="email" value={clientEmail} onChange={e => setClientEmail(e.target.value)} placeholder="client@example.com" className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-893" />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div data-id="element-894">
                      <h3 className="font-bold text-neutral-text mb-4 flex items-center gap-2" data-id="element-895">
                        <TrendingUp className="w-5 h-5 text-secondary" data-id="element-896" />
                        Lead Information
                      </h3>

                      <div className="space-y-4" data-id="element-897">
                        <div className="grid grid-cols-2 gap-3" data-id="element-898">
                          <div data-id="element-899">
                            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-900">
                              Lead Source
                            </label>
                            <select value={leadSource} onChange={e => setLeadSource(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary bg-white" data-id="element-901">
                              <option value="" data-id="element-902">Select source</option>
                              <option value="facebook" data-id="element-903">Facebook</option>
                              <option value="instagram" data-id="element-904">Instagram</option>
                              <option value="google" data-id="element-905">Google Ads</option>
                              <option value="referral" data-id="element-906">Referral</option>
                              <option value="website" data-id="element-907">Website</option>
                              <option value="walk-in" data-id="element-908">Walk-in</option>
                              <option value="other" data-id="element-909">Other</option>
                            </select>
                          </div>

                          <div data-id="element-910">
                            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-911">
                              Campaign
                            </label>
                            <input type="text" value={marketingCampaign} onChange={e => setMarketingCampaign(e.target.value)} placeholder="Campaign ID" className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-912" />
                          </div>
                        </div>

                        <div data-id="element-913">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-914">
                            Notes
                          </label>
                          <div className="relative" data-id="element-915">
                            <MessageSquare className="absolute left-4 top-4 w-5 h-5 text-gray-400" data-id="element-916" />
                            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add any additional notes about the client..." rows={4} className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary resize-none" data-id="element-917" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>}

                {/* Step 2: Search Criteria - Updated */}
                {addClientStep === 2 && <div className="p-6 space-y-5" data-id="element-918">
                    <div data-id="element-919">
                      <h3 className="font-bold text-neutral-text mb-4 flex items-center gap-2" data-id="element-920">
                        <Home className="w-5 h-5 text-secondary" data-id="element-921" />
                        Search Criteria
                      </h3>

                      <div className="space-y-4" data-id="element-922">
                        {/* Transaction Type & Client Stage - Row */}
                        <div className="grid grid-cols-2 gap-3" data-id="element-923">
                          <div data-id="element-924">
                            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-925">
                              Transaction Type
                            </label>
                            <select value={transactionType} onChange={e => setTransactionType(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary bg-white" data-id="element-926">
                              <option value="BUY" data-id="element-927">Buy</option>
                              <option value="RENT" data-id="element-928">Rent</option>
                            </select>
                          </div>

                          <div data-id="element-929">
                            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-930">
                              Client Stage
                            </label>
                            <select value={clientStage} onChange={e => setClientStage(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary bg-white" data-id="element-931">
                              <option value="New" data-id="element-932">New</option>
                              <option value="In-contact" data-id="element-933">In Contact</option>
                              <option value="Negotiating" data-id="element-934">Negotiating</option>
                              <option value="Confirmed" data-id="element-935">Confirmed</option>
                              <option value="Closed" data-id="element-936">Closed</option>
                            </select>
                          </div>
                        </div>

                        {/* Interested Projects - Multi-select */}
                        <div data-id="element-937">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-938">
                            Interested Projects
                          </label>
                          <div className="border border-gray-200 rounded-xl p-3 bg-white" data-id="element-939">
                            {interestedProjects.length > 0 && <div className="flex flex-wrap gap-2 mb-3" data-id="element-940">
                                {interestedProjects.map(project => <span key={project} className="inline-flex items-center gap-1 px-3 py-1 bg-secondary/10 text-secondary rounded-full text-sm font-medium" data-id="element-941">
                                    {project}
                                    <button onClick={() => toggleProject(project)} className="hover:bg-secondary/20 rounded-full p-0.5" data-id="element-942">
                                      <X className="w-3 h-3" data-id="element-943" />
                                    </button>
                                  </span>)}
                              </div>}
                            <select value="" onChange={e => {
                        if (e.target.value) {
                          toggleProject(e.target.value);
                        }
                      }} className="w-full px-0 py-2 border-0 focus:outline-none focus:ring-0 bg-transparent text-gray-500" data-id="element-944">
                              <option value="" data-id="element-945">Select projects...</option>
                              {availableProjects.filter(p => !interestedProjects.includes(p)).map(project => <option key={project} value={project} data-id="element-946">
                                    {project}
                                  </option>)}
                            </select>
                          </div>
                        </div>

                        {/* Bedrooms & Bathrooms - Row */}
                        <div className="grid grid-cols-2 gap-3" data-id="element-947">
                          <div data-id="element-948">
                            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-949">
                              Bedrooms
                            </label>
                            <select value={bedrooms} onChange={e => setBedrooms(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary bg-white" data-id="element-950">
                              <option value="" data-id="element-951">Select</option>
                              <option value="1" data-id="element-952">1</option>
                              <option value="2" data-id="element-953">2</option>
                              <option value="3" data-id="element-954">3</option>
                              <option value="4" data-id="element-955">4</option>
                              <option value="5+" data-id="element-956">5+</option>
                            </select>
                          </div>

                          <div data-id="element-957">
                            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-958">
                              Bathrooms
                            </label>
                            <select value={bathrooms} onChange={e => setBathrooms(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary bg-white" data-id="element-959">
                              <option value="" data-id="element-960">Select</option>
                              <option value="1" data-id="element-961">1</option>
                              <option value="2" data-id="element-962">2</option>
                              <option value="3" data-id="element-963">3</option>
                              <option value="4" data-id="element-964">4</option>
                              <option value="5+" data-id="element-965">5+</option>
                            </select>
                          </div>
                        </div>

                        {/* Budget Range - Row */}
                        <div className="grid grid-cols-2 gap-3" data-id="element-966">
                          <div data-id="element-967">
                            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-968">
                              Min Budget
                            </label>
                            <div className="relative" data-id="element-969">
                              <input type="number" value={minBudget} onChange={e => setMinBudget(e.target.value)} placeholder="0" className="w-full px-4 py-3 pr-14 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-970" />
                              <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm font-medium" data-id="element-971">
                                EGP
                              </span>
                            </div>
                          </div>

                          <div data-id="element-972">
                            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-973">
                              Max Budget
                            </label>
                            <div className="relative" data-id="element-974">
                              <input type="number" value={maxBudget} onChange={e => setMaxBudget(e.target.value)} placeholder="0" className="w-full px-4 py-3 pr-14 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-975" />
                              <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm font-medium" data-id="element-976">
                                EGP
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Area Range - Row */}
                        <div className="grid grid-cols-2 gap-3" data-id="element-977">
                          <div data-id="element-978">
                            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-979">
                              Min Area
                            </label>
                            <div className="relative" data-id="element-980">
                              <input type="number" value={minArea} onChange={e => setMinArea(e.target.value)} placeholder="0" className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-981" />
                              <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm font-medium" data-id="element-982">
                                m²
                              </span>
                            </div>
                          </div>

                          <div data-id="element-983">
                            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-984">
                              Max Area
                            </label>
                            <div className="relative" data-id="element-985">
                              <input type="number" value={maxArea} onChange={e => setMaxArea(e.target.value)} placeholder="0" className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-986" />
                              <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm font-medium" data-id="element-987">
                                m²
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Location */}
                        <div data-id="element-988">
                          <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-989">
                            Preferred Location
                          </label>
                          <select value={location} onChange={e => setLocation(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary bg-white" data-id="element-990">
                            <option value="" data-id="element-991">
                              Search for area (e.g., 5th Settlement)
                            </option>
                            <option value="new-cairo" data-id="element-992">New Cairo</option>
                            <option value="5th-settlement" data-id="element-993">
                              5th Settlement
                            </option>
                            <option value="madinaty" data-id="element-994">Madinaty</option>
                            <option value="rehab" data-id="element-995">Rehab City</option>
                            <option value="6th-october" data-id="element-996">6th of October</option>
                            <option value="sheikh-zayed" data-id="element-997">Sheikh Zayed</option>
                            <option value="new-capital" data-id="element-998">
                              New Administrative Capital
                            </option>
                            <option value="north-coast" data-id="element-999">North Coast</option>
                            <option value="ain-sokhna" data-id="element-1000">Ain Sokhna</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>}
              </div>

              {/* Footer - Fixed with Next/Back/Submit buttons */}
              <div className="flex-shrink-0 p-6 border-t border-gray-100 bg-white" data-id="element-1001">
                {addClientStep === 1 ? <button onClick={handleNextStep} disabled={!clientName || !clientPhone} className="w-full bg-secondary hover:bg-secondary-dark text-white font-bold py-4 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1002">
                    Next: Search Criteria
                    <ChevronRight className="w-5 h-5" data-id="element-1003" />
                  </button> : <div className="flex gap-3" data-id="element-1004">
                    <button onClick={handlePreviousStep} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-4 rounded-xl transition-colors" data-id="element-1005">
                      Back
                    </button>
                    <button onClick={handleSubmitClient} className="flex-1 bg-secondary hover:bg-secondary-dark text-white font-bold py-4 rounded-xl transition-colors" data-id="element-1006">
                      Add Client
                    </button>
                  </div>}
              </div>
            </div>
          </div>}

        {/* Advanced Search Modal */}
        {showAdvancedSearch && <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-id="element-1007">
            <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl flex flex-col max-h-[90vh]" data-id="element-1008">
              {/* Header */}
              <div className="flex-shrink-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between rounded-t-3xl sm:rounded-t-2xl" data-id="element-1009">
                <h2 className="text-xl font-bold text-neutral-text" data-id="element-1010">
                  Advanced Search
                </h2>
                <button onClick={() => setShowAdvancedSearch(false)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-neutral-bg rounded-full transition-colors" data-id="element-1011">
                  <X className="w-5 h-5" data-id="element-1012" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-1013">
                {/* Assigned Date Range */}
                <div data-id="element-1014">
                  <label className="block text-sm font-bold text-neutral-text mb-3 flex items-center gap-2" data-id="element-1015">
                    <Calendar className="w-4 h-4 text-secondary" data-id="element-1016" />
                    Assigned Date Range
                  </label>
                  <div className="grid grid-cols-2 gap-3" data-id="element-1017">
                    <div data-id="element-1018">
                      <label className="block text-xs text-gray-600 mb-1" data-id="element-1019">
                        From
                      </label>
                      <input type="date" value={assignedDateFrom} onChange={e => setAssignedDateFrom(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-1020" />
                    </div>
                    <div data-id="element-1021">
                      <label className="block text-xs text-gray-600 mb-1" data-id="element-1022">
                        To
                      </label>
                      <input type="date" value={assignedDateTo} onChange={e => setAssignedDateTo(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-1023" />
                    </div>
                  </div>
                </div>

                {/* Phone Country */}
                <div data-id="element-1024">
                  <label className="block text-sm font-bold text-neutral-text mb-3 flex items-center gap-2" data-id="element-1025">
                    <Phone className="w-4 h-4 text-secondary" data-id="element-1026" />
                    Phone Country
                  </label>
                  <div className="flex gap-2 mb-3" data-id="element-1027">
                    <button onClick={() => setPhoneCountryFilter('is')} className={`flex-1 py-2 px-4 rounded-lg text-sm font-semibold transition-colors ${phoneCountryFilter === 'is' ? 'bg-secondary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-1028">
                      Is
                    </button>
                    <button onClick={() => setPhoneCountryFilter('is not')} className={`flex-1 py-2 px-4 rounded-lg text-sm font-semibold transition-colors ${phoneCountryFilter === 'is not' ? 'bg-secondary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-1029">
                      Is Not
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-1030">
                    {availableCountries.map(country => <button key={country} onClick={() => toggleCountry(country)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border-2 ${selectedCountries.includes(country) ? 'bg-secondary text-white border-secondary' : 'bg-white text-gray-600 border-gray-200 hover:border-secondary'}`} data-id="element-1031">
                        {selectedCountries.includes(country) && <Check className="w-3 h-3 inline mr-1" data-id="element-1032" />}
                        {country}
                      </button>)}
                  </div>
                </div>

                {/* Sales Stage */}
                <div data-id="element-1033">
                  <label className="block text-sm font-bold text-neutral-text mb-3 flex items-center gap-2" data-id="element-1034">
                    <TrendingUp className="w-4 h-4 text-secondary" data-id="element-1035" />
                    Sales Stage (Multi-select)
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-1036">
                    {availableSalesStages.map(stage => <button key={stage} onClick={() => toggleSalesStage(stage)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border-2 ${selectedSalesStages.includes(stage) ? 'bg-secondary text-white border-secondary' : 'bg-white text-gray-600 border-gray-200 hover:border-secondary'}`} data-id="element-1037">
                        {selectedSalesStages.includes(stage) && <Check className="w-3 h-3 inline mr-1" data-id="element-1038" />}
                        {stage}
                      </button>)}
                  </div>
                </div>

                {/* Tags */}
                <div data-id="element-1039">
                  <label className="block text-sm font-bold text-neutral-text mb-3 flex items-center gap-2" data-id="element-1040">
                    <Tag className="w-4 h-4 text-secondary" data-id="element-1041" />
                    Tags
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-1042">
                    {availableTags.map(tag => <button key={tag} onClick={() => toggleTag(tag)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border-2 ${selectedTags.includes(tag) ? 'bg-secondary text-white border-secondary' : 'bg-white text-gray-600 border-gray-200 hover:border-secondary'}`} data-id="element-1043">
                        {selectedTags.includes(tag) && <Check className="w-3 h-3 inline mr-1" data-id="element-1044" />}
                        {tag}
                      </button>)}
                  </div>
                </div>

                {/* Labels */}
                <div data-id="element-1045">
                  <label className="block text-sm font-bold text-neutral-text mb-3 flex items-center gap-2" data-id="element-1046">
                    <MessageSquare className="w-4 h-4 text-secondary" data-id="element-1047" />
                    Labels
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-1048">
                    {availableLabels.map(label => <button key={label} onClick={() => toggleLabel(label)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border-2 ${selectedLabels.includes(label) ? 'bg-secondary text-white border-secondary' : 'bg-white text-gray-600 border-gray-200 hover:border-secondary'}`} data-id="element-1049">
                        {selectedLabels.includes(label) && <Check className="w-3 h-3 inline mr-1" data-id="element-1050" />}
                        {label}
                      </button>)}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 p-6 border-t border-gray-100" data-id="element-1051">
                <div className="flex gap-3" data-id="element-1052">
                  <button onClick={handleClearAdvancedFilters} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-3 rounded-xl transition-colors" data-id="element-1053">
                    Clear All
                  </button>
                  <button onClick={handleApplyAdvancedFilters} className="flex-1 bg-secondary hover:bg-secondary-dark text-white font-bold py-3 rounded-xl transition-colors" data-id="element-1054">
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          </div>}

        <BottomNav onNetworkClick={onNetworkClick} onInventoryClick={onInventoryClick} onRMIClick={onRMIClick} onNotificationsClick={onNotificationsClick} onProfileClick={onProfileClick} data-id="element-1055" />
      </div>
    </div>;
}